import React, { useMemo, useState, useCallback, useRef } from "react";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";

import FormContentManager from "../../components/common/form/FormContentManager";
import UserForm from "../../components/common/form/UserForm";

import PlanEditor from "../../components/safety/editor/ReportEditor";
import ChecklistEditor from "../../components/safety/editor/ChecklistEditor";
import RiskcheckEditor from "../../components/safety/editor/RiskcheckEditor";
import IncidentEditor from "../../components/safety/editor/IncidentEditor";
import SCAREditor from "../../components/safety/editor/SCAREditor";

// 백엔드 저장용 form_type → DNS prefix 유지
const DOC_LABELS = {
  SPP: "안전 계획",
  ISC: "점검 일정",
  RAS: "위험성 평가",
  INC: "사고 이력",
  CAR: "안전 시정 조치 요구서",
};

// 프런트에서 어떤 에디터를 띄울지 URL 기반으로 선택
const MODE_TO_EDITOR = {
  report: PlanEditor,
  check: ChecklistEditor,
  riskcheck: RiskcheckEditor,
  incident: IncidentEditor,
  scar: SCAREditor,
};

// URL `mode` → default form_type 매핑 (DNS prefix 그대로)
const MODE_TO_FORMTYPE = {
  report: "SPP",
  check: "ISC",
  riskcheck: "RAS",
  incident: "INC",
  scar: "CAR",
};

// form_type → scope(백엔드 저장용)
const SCOPE_BY_FORM = (ft) => {
  if (ft === "SPP") return "report";
  if (ft === "ISC") return "check";
  if (ft === "RAS") return "riskcheck";
  if (ft === "INC") return "incident";
  if (ft === "CAR") return "scar";
  return "report";
};

export default function SafetyForm() {
  const approvalRef = useRef();
  const [approvalMode] = useState("normal");

  const navigate = useNavigate();
  const projectId = localStorage.getItem("projectId");

  // ✅ FormViewManager와 동일: 로그인 사용자(localStorage.user)
  const currentUser = useMemo(() => {
    try {
      const raw = localStorage.getItem("user");
      return raw ? JSON.parse(raw) : {};
    } catch {
      return {};
    }
  }, []);

  const { mode } = useParams(); // URL 기반 Editor 선택 (report/check/...)
  const [sp] = useSearchParams();

  // 1) form_type: 백엔드 DNS prefix용 → 반드시 3글자 유지
  //    query 파라미터에 form_type이 있으면 override 가능
  const formType = (
    sp.get("form_type") ||
    MODE_TO_FORMTYPE[mode] ||
    "SPP"
  ).toUpperCase();

  // 2) Editor 선택: URL 기반
  const StdForm = MODE_TO_EDITOR[mode];

  // 3) label, scope 등 백엔드/프런트 공용 메타
  const docLabel = DOC_LABELS[formType] || formType;
  const scope = SCOPE_BY_FORM(formType);

  // stage (optional)
  const stage = (sp.get("stage") || "").toUpperCase();

  // 제목 생성
  const title = useMemo(() => {
    const sub = stage ? ` · ${stage}` : "";
    return `${docLabel} 작성${sub}`;
  }, [docLabel, stage]);

  // Editor가 만들어내는 데이터
  const [stdBody, setStdBody] = useState(null);

  // 저장 body 구성
  const getBody = useCallback(
    () => ({
      layout: null,
      values: {
        project: projectId,
        module: "safety",
        doc_type: scope, // 백엔드 저장용
        title: (stdBody?.title || stdBody?.summary?.title || "").trim(),
        status: stdBody?.meta?.status || "draft",
        meta: stdBody?.meta || {}, // Editor에서 넘어온 값
      },
    }),
    [projectId, formType, stdBody]
  );

  return (
    <div className="">
      {/* 사용자 템플릿 모드 */}
      {mode === "template" && (
        <UserForm
          mode={2}
          module="safety"
          formType={formType}
          scope={scope}
          project={projectId}
          defaultTitle={`${docLabel} (사용자 템플릿)`}
        />
      )}

      {/* 표준 템플릿 모드 */}
      {mode !== "template" && StdForm && (
        <FormContentManager
          params={{
            module: "safety",
            project: projectId,
            doc_type: formType,
            stage,
          }}
          docName={docLabel}
          docDescribe={`${docLabel} 작성 및 저장`}
          getBody={getBody}
          onChangeId={(dns) => console.log("생성된 DNS:", dns)}
          summaryConfig={{ showPhotoSummary: true }}
          requestPeople={stdBody?.meta?.referenceUserDetails ?? []}
        >
          <StdForm
            projectId={projectId}
            formType={formType}
            docLabel={docLabel}
            initial={{ subtype: stage }}
            readOnly={false}
            onChange={setStdBody}
            currentUser={currentUser}
          />
        </FormContentManager>
      )}
    </div>
  );
}
